import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { LocationsService } from './locations.service';
import { MasterService } from '../../../master/services/master.service';
import { MasterModel } from '../../../master/models/master.model';
import { MerchantLocationGeneralDto, DEFAULT_MERCHANT_LOCATION_GENERAL } from '../models/merchant-location-general.dto';

export interface CatalogsOptions {
  countries: MasterModel[];
  provinces: MasterModel[];
  locationStatuses: MasterModel[];
  locationCategories: MasterModel[];
  timeZones: MasterModel[];
  currencyCodes: MasterModel[];
}

@Injectable()
export class LocationsFacade {
  constructor(
    private locationsService: LocationsService,
    private masterService: MasterService
  ) {}

  loadGeneralFormCatalogs(): Observable<CatalogsOptions> {
    return forkJoin({
      countries: this.masterService.getCountries().pipe(
        catchError(error => {
          console.error('Error cargando países:', error);
          return of([]);
        })
      ),
      provinces: this.masterService.getProvinces().pipe(
        catchError(error => {
          console.error('Error cargando provincias:', error);
          return of([]);
        })
      ),
      locationStatuses: this.masterService.getMerchantLocationsStatuses().pipe(
        catchError(error => {
          console.error('Error cargando estados:', error);
          return of([]);
        })
      ),
      locationCategories: this.masterService.getMerchantLocationCategories().pipe(
        catchError(error => {
          console.error('Error cargando categorías:', error);
          return of([]);
        })
      ),
      timeZones: of(this.getTimeZonesStaticData()),
      currencyCodes: of(this.getCurrencyCodesStaticData())
    });
  }

  loadMunicipalities(provinceCode: string): Observable<MasterModel[]> {
    if (!provinceCode) {
      return of([]);
    }
    return this.masterService.getMunicipalities(provinceCode).pipe(
      catchError(error => {
        console.error('Error cargando municipios:', error);
        return of([]);
      })
    );
  }

  loadLocationById(locationId: string): Observable<any> {
    return this.locationsService.getLocationByUid(locationId).pipe(
      map(response => response.value)
    );
  }

  loadAllLocations(): Observable<any[]> {
    return this.locationsService.getAll().pipe(
      map(response => response || []),
      catchError(error => {
        console.error('Error cargando localizaciones:', error);
        return of([]);
      })
    );
  }

  createLocation(data: MerchantLocationGeneralDto): Observable<any> {
    return this.locationsService.create(data);
  }

  updateLocationGeneral(locationId: string, data: MerchantLocationGeneralDto): Observable<any> {
    const updateData = {
      ...data,
      merchantLocationId: locationId
    };
    return this.locationsService.updateWithPayload(locationId, updateData);
  }

  applyDefaultValues(
    currentData: Partial<MerchantLocationGeneralDto>,
    catalogs: CatalogsOptions
  ): any {
    const countryDefault = catalogs.countries.find(c => c.id === 'ES');
    const currencyDefault = catalogs.currencyCodes.find(c => c.id === 'EUR');
    const timeZoneDefault = catalogs.timeZones.find(tz => tz.id === 'Europe/Madrid');
    
    return {
      ...currentData,
      countryCode: currentData.countryCode || countryDefault,
      currencyCode: currentData.currencyCode || currencyDefault,
      timeZoneId: currentData.timeZoneId || timeZoneDefault,
      isMainLocation: currentData.isMainLocation !== undefined ? currentData.isMainLocation : false,
      displayOrder: currentData.displayOrder !== undefined ? currentData.displayOrder : 0
    };
  }


  private getTimeZonesStaticData(): MasterModel[] {
    return [
      { id: 'Europe/Madrid', display: 'Europe/Madrid (Península y Baleares, UTC+1)' },
      { id: 'Atlantic/Canary', display: 'Atlantic/Canary (Canarias, UTC+0)' }
    ];
  }

  private getCurrencyCodesStaticData(): MasterModel[] {
    return [
      { id: 'EUR', display: 'EUR - Euro' },
    ];
  }
}
